import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Scale } from 'lucide-react';
import { type DriftedTaskView, RebalanceOnDriftOutput } from '@apm/contracts';
import { displayDate } from '../../../lib/date';
import { AiSkillPanel } from '../../ai/components/ai-skill-panel';
import { EstimateCard } from '../../ai/components/estimate-card';

/**
 * Hộp "Cân bằng": việc này trễ thì sao, và nên xử lý thế nào — APM-AI-z.
 *
 * ## Hai lớp, và lớp đầu KHÔNG cần AI
 *
 * 1. **Cách xử lý hệ thống đề xuất** — phương án do solver tất định dựng và
 *    kiểm năng lực, đọc thẳng từ `/rebalance/drift` (đã có sẵn trong cache của
 *    tab Công việc). Luôn hiện, kể cả khi tổ chức chưa có key AI: từ
 *    2026-09-23 dải "việc đang chậm deadline" không còn bày phương án, nên đây
 *    là chỗ duy nhất người dùng thấy chúng.
 * 2. **Phân tích của AI** — lớp thêm, cần key: ngày quan trọng nào bị lùi, việc
 *    nào phải chờ việc này, và vì sao ước lượng như vậy.
 *
 * Hai lớp tách hẳn nhau có chủ đích: trộn vào một danh sách là để người dùng
 * không phân biệt được đâu là con số hệ thống tính và đâu là lời model viết.
 *
 * Không có nút áp dụng: người dùng đọc, chọn, rồi tự sửa lịch.
 */
export function RebalanceAiSection({
  projectId,
  tasks,
  defaultOpen = false,
}: {
  readonly projectId: string;
  /** Mở sẵn — dùng khi khối này **là** nội dung của một hộp thoại. */
  readonly defaultOpen?: boolean;
  /** Việc đang chậm deadline, kèm phương án của solver — cùng dữ liệu với dải cảnh báo. */
  readonly tasks: readonly DriftedTaskView[];
}): ReactNode {
  const { t } = useTranslation();
  const [taskId, setTaskId] = useState('');
  const picked = tasks.find((task) => task.taskId === taskId) ?? null;

  // Mở từ menu "Sửa nhanh" lúc không có việc nào trễ: nói ra, đừng để một ô
  // chọn rỗng tự giải thích.
  if (tasks.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('rebalanceAi.noLateTasks')}</p>;
  }

  return (
    <AiSkillPanel
      defaultOpen={defaultOpen}
      /*
        Không có đường làm tay tương đương — APM-PSET-k.

        Phương án thì có — lớp "cách xử lý hệ thống đề xuất" ngay trong
        `controls`, hiện cả khi chưa có key. Nhưng *câu trả lời vì sao* thì
        không: chỉ sang danh sách phương án là đưa người dùng tới đúng thứ họ
        vừa đọc xong và vẫn chưa hiểu.
      */
      offlineMode="needKey"
      skill="rebalance_on_drift"
      projectId={projectId}
      icon={Scale}
      title={t('rebalanceAi.title')}
      help={
        // Từng ý một dòng — một đoạn liền ba ý là thứ người ta đọc lướt rồi bỏ.
        <div className="flex flex-col gap-1.5">
          <p>{t('rebalanceAi.helpIntro')}</p>
          <ul className="flex list-disc flex-col gap-1 pl-5">
            <li>{t('rebalanceAi.helpDates')}</li>
            <li>{t('rebalanceAi.helpWaiting')}</li>
            <li>{t('rebalanceAi.helpOptions')}</li>
          </ul>
          <p>{t('rebalanceAi.helpNote')}</p>
        </div>
      }
      label={t('rebalanceAi.run')}
      prompt={t('rebalanceAi.prompt')}
      params={{ taskId }}
      canRun={taskId !== ''}
      schema={RebalanceOnDriftOutput}
      routeParams={{ projectId }}
      controls={
        <div className="flex flex-col gap-3">
          <select
            value={taskId}
            onChange={(event) => setTaskId(event.target.value)}
            aria-label={t('rebalanceAi.pickTask')}
            className="h-9 w-full rounded-DEFAULT border border-input bg-background px-2 text-sm"
          >
            <option value="">{t('rebalanceAi.pickTask')}</option>
            {tasks.map((task) => (
              <option key={task.taskId} value={task.taskId}>
                {task.taskKey} — {task.title}
              </option>
            ))}
          </select>

          {picked ? <SolverOptions task={picked} /> : null}
        </div>
      }
      render={(content) => (
        <div className="flex flex-col gap-2">
          <p className="text-sm">
            {t(`rebalanceAi.cause.${content.trigger.cause}`, {
              key: content.trigger.taskKey,
              days: content.trigger.deltaDays,
            })}
          </p>

          {content.impact.milestonesAtRisk.length > 0 ? (
            <div className="rounded-DEFAULT border border-rag-amber/40 bg-rag-amber/5 p-2">
              <p className="text-xs font-medium text-rag-amber">{t('rebalanceAi.milestones')}</p>
              <ul className="mt-0.5 flex list-disc flex-col gap-0.5 pl-5 text-xs">
                {content.impact.milestonesAtRisk.map((milestone, index) => (
                  <li key={index}>{milestone}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <ul className="flex flex-col gap-2">
            {content.options.map((option) => (
              <li
                key={option.name}
                className="rounded-DEFAULT border border-border bg-background p-2"
              >
                <p className="text-sm font-medium">{option.label}</p>
                <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">
                  {t('rebalanceAi.shift', { value: option.p50ShiftDays })}
                  {' · '}
                  {t('rebalanceAi.touches', { count: option.touchesPeople.length })}
                  {' · '}
                  {t('rebalanceAi.moves', { count: option.moves.length })}
                </p>
                {/* Ước lượng hiện qua đúng một component chung — APM-AI-y §0.4:
                    dẫn chứng trước, con số sau. */}
                <EstimateCard estimate={option.estimate} className="mt-1" />
              </li>
            ))}
          </ul>
        </div>
      )}
    />
  );
}

/**
 * Cách xử lý hệ thống đề xuất cho một việc — không cần AI.
 *
 * Mỗi phương án nói bằng lời thường: lùi mấy ngày làm việc, ảnh hưởng mấy
 * người, và từng việc đổi thế nào (lùi sang ngày nào, giao cho ai, hay gỡ khỏi
 * lịch). Không có phương án nào cũng phải nói ra.
 */
function SolverOptions({ task }: { readonly task: DriftedTaskView }): ReactNode {
  const { t } = useTranslation();

  return (
    <section className="flex flex-col gap-2" aria-label={t('rebalanceAi.solverTitle')}>
      <h4 className="text-xs font-medium text-muted-foreground">{t('rebalanceAi.solverTitle')}</h4>

      {task.options.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('rebalanceAi.noOption')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {task.options.map((option) => (
            <li
              key={option.name}
              className="rounded-DEFAULT border border-border bg-background p-2"
            >
              <p className="text-sm font-medium">{option.label}</p>
              <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">
                {option.p50ShiftDays === 0
                  ? t('rebalanceAi.noShift')
                  : t('rebalanceAi.shift', { value: Math.abs(option.p50ShiftDays) })}
                {' · '}
                {t('rebalanceAi.touches', { count: option.touchesPeople.length })}
              </p>
              {option.moves.length > 0 ? (
                <ul className="mt-1 flex flex-col gap-0.5 text-xs">
                  {option.moves.map((move, index) => (
                    <li key={`${move.taskKey}-${index}`}>
                      <span className="text-muted-foreground">{move.taskKey}: </span>
                      {move.to === null
                        ? t('rebalanceAi.moveUnschedule')
                        : move.assigneeName
                          ? t('rebalanceAi.moveAssign', { name: move.assigneeName })
                          : t('rebalanceAi.moveDate', {
                              from: displayDate(move.from),
                              to: displayDate(move.to),
                            })}
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
